import type { AgUiEvent } from './types';

/**
 * Agent slugs with a dedicated AG-UI live route on the graph service
 * (`/agents/<slug>/run/live`), mirrored by the Next.js proxy at
 * `/api/agents/<slug>/run/live`.
 */
export const AGENT_LIVE_SLUGS = [
  'crisis',
  'outreach',
  'social',
  'translate',
  'thought-leadership',
] as const;

export type AgentLiveSlug = (typeof AGENT_LIVE_SLUGS)[number];

export function isAgentLiveSlug(value: string): value is AgentLiveSlug {
  return (AGENT_LIVE_SLUGS as readonly string[]).includes(value);
}

export type AgentLiveStatus = 'running' | 'waiting_approval' | 'completed' | 'failed';

/**
 * Mirrors `AgentLiveAgUiState` from
 * langgraph-service/src/agents/shared/liveMode.ts.
 */
export type AgentLiveAgUiState = {
  agentId: string;
  label: string;
  requestId: string;
  runId: string;
  threadId: string;
  clientId: string;
  status: AgentLiveStatus;
  stage: string;
  summary: string;
  currentNode: string | null;
  completedNodes: string[];
  pipelineNodes: string[];
  draftContent: string;
  title: string;
  excerpt: string;
  /**
   * True when the run finished but human approval is still required
   * (crisis HITL). The approval round-trip uses the existing non-live
   * mechanism (a `set_approval` run) — not the live channel.
   */
  approvalPending: boolean;
  approvalContext: Record<string, unknown> | null;
  extras: Record<string, unknown>;
  sourceConversationId: string | null;
  error: string | null;
};

type ActivitySnapshot = {
  currentNode?: string | null;
  currentStage?: string;
  pipelineStages?: string[];
  completedStages?: string[];
};

/**
 * Reduces an AG-UI event into the agent live state. STATE_SNAPSHOT is
 * authoritative; TEXT_MESSAGE_* progressively fill `draftContent`;
 * ACTIVITY_SNAPSHOT refreshes node progress; RUN_ERROR marks failure.
 */
export function reduceAgentLiveAgUiEvent(
  current: AgentLiveAgUiState | null,
  event: AgUiEvent<AgentLiveAgUiState>,
): AgentLiveAgUiState | null {
  if (event.type === 'STATE_SNAPSHOT') {
    const snapshot = (event as { snapshot?: AgentLiveAgUiState }).snapshot;
    if (snapshot && typeof snapshot === 'object' && typeof snapshot.agentId === 'string') {
      return snapshot;
    }
    return current;
  }

  if (!current) return current;

  if (event.type === 'TEXT_MESSAGE_START') {
    return { ...current, draftContent: '' };
  }

  if (event.type === 'TEXT_MESSAGE_CONTENT') {
    const delta = (event as { delta?: unknown }).delta;
    if (typeof delta === 'string' && delta) {
      return { ...current, draftContent: `${current.draftContent}${delta}` };
    }
    return current;
  }

  if (event.type === 'ACTIVITY_SNAPSHOT') {
    const activity = ((event as { activity?: ActivitySnapshot }).activity || {}) as ActivitySnapshot;
    return {
      ...current,
      ...(typeof activity.currentNode === 'string' || activity.currentNode === null
        ? { currentNode: activity.currentNode }
        : {}),
      ...(typeof activity.currentStage === 'string' && activity.currentStage
        ? { stage: activity.currentStage }
        : {}),
    };
  }

  if (event.type === 'RUN_ERROR') {
    const message = (event as { message?: unknown }).message;
    return {
      ...current,
      status: 'failed',
      error: typeof message === 'string' && message ? message : current.error || 'agent_live_failed',
    };
  }

  return current;
}
